import React, { useState, useEffect } from "react";
import DiscountToggle from "./discount-toggle";
import VisualDateRangePicker from "./visual-date-range-picker";
import {
  formatCurrency,
  formatPercent,
  calculateFinalPrice,
  getDefaultSelectedOptions,
} from "./ctf-shared-utils";

/**
 * Aircraft Pricing Card component
 * Updated for ClickToFlyResult model structure
 */
const AircraftPricingCard = ({
  aircraft,
  tripType,
  departureDate,
  departureAirportCode,
  arrivalAirportCode,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState({
    member: null,
    tdr: null,
    airportRadius: null,
    yom: null,
    departureTimeLeg1: null,
    departureTimeLeg2: null,
    wifiEnabled: false,
  });

  const [calculatedPrice, setCalculatedPrice] = useState(aircraft.defaultPrice);

  // Calculate price whenever selections change
  useEffect(() => {
    const finalPrice = calculateFinalPrice(aircraft, selectedOptions);
    setCalculatedPrice(finalPrice);
  }, [selectedOptions, aircraft]);

  const handleOptionSelect = (category, option) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [category]: option,
    }));
  };

  // Prepare member options using toggleOptions structure
  const memberToggle = aircraft.toggleOptions?.member;
  const isMember = memberToggle?.isMember || false;

  const memberOptions = [
    {
      label: "Non-Member",
      value: false,
      change: memberToggle?.nonMemberDiscount || { percent: 0, dollars: 0 },
      isCurrent: !isMember,
    },
    {
      label: "Member",
      value: true,
      change: memberToggle?.memberDiscount || { percent: 0, dollars: 0 },
      isCurrent: isMember,
    },
  ];

  // Set default member and YOM options
  useEffect(() => {
    const defaultOptions = getDefaultSelectedOptions(aircraft);

    // Auto-select default YOM with WiFi info
    const defaultYomOption = aircraft.toggleOptions?.yom?.options?.find(
      (o) => o.isDefault
    );

    setSelectedOptions((prev) => ({
      ...prev,
      member: defaultOptions.member,
      yom: defaultYomOption
        ? {
            label: `${defaultYomOption.year} (Default)`,
            value: defaultYomOption.year,
            year: defaultYomOption.year,
            change: defaultYomOption.change || { percent: 0, dollars: 0 },
            isIncrease: defaultYomOption.isIncrease,
            isDefault: defaultYomOption.isDefault,
            isDiscount: defaultYomOption.isDiscount,
            wifi: defaultYomOption.wifi,
          }
        : null,
      wifiEnabled: false,
    }));
  }, [aircraft.aircraftTypeId]);

  // Get seat count from aircraft data
  const seatCount = aircraft.seatCount ? `${aircraft.seatCount}` : "N/A";

  return (
    <div className="aircraft-pricing-card">
      <div
        className="aircraft-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="aircraft-summary">
          <h3 className="aircraft-name">{aircraft.aircraftTypeName}</h3>
          <div className="aircraft-meta">
            <span className="seat-count">{seatCount} seats</span>
          </div>
        </div>
        <div className="pricing-summary-compact">
          <div className="price-display">
            <div className="starting-from-price">
              <label>Starting From:</label>
              <span className="price">
                {formatCurrency(
                  aircraft.theoreticalMaxDiscount?.lowestPossiblePrice ||
                    aircraft.defaultPrice
                )}
              </span>
            </div>
            <div className="default-price">
              <label>Default:</label>
              <span className="price">
                {formatCurrency(aircraft.defaultPrice)}
              </span>
            </div>
          </div>
          <button className="expand-btn" type="button">
            {isExpanded ? "Hide Options ▲" : "Show Options ▼"}
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="aircraft-details">
          <div className="max-savings-info">
            <strong>Max Savings:</strong>{" "}
            {formatPercent(aircraft.theoreticalMaxDiscount?.percent || 0)} (
            {formatCurrency(aircraft.theoreticalMaxDiscount?.dollars || 0)})
          </div>

          <div className="discount-toggles">
            {/* Member Discount */}
            <DiscountToggle
              category="Member"
              options={memberOptions}
              selectedValue={selectedOptions.member}
              onSelect={(option) => handleOptionSelect("member", option)}
            />

            {/* TDR Flexibility with Visual Date Range Picker (if available) */}
            {aircraft.toggleOptions?.departureTdr?.available && (
              <VisualDateRangePicker
                options={aircraft.toggleOptions.departureTdr.options}
                selectedValue={selectedOptions.tdr}
                onSelect={(option) => handleOptionSelect("tdr", option)}
                defaultPrice={aircraft.defaultPrice}
                departureDate={departureDate}
              />
            )}

            {/* Airport Radius - Flexible airport based on highest discount */}
            <DiscountToggle
              category={
                aircraft.toggleOptions?.alternateAirports?.flexibleAirportEnd === "arrival"
                  ? "Arrival Airport Flexibility"
                  : "Departure Airport Flexibility"
              }
              options={(
                aircraft.toggleOptions?.alternateAirports?.options || []
              ).map((opt) => ({
                label: opt.radius === 0 ? "Exact Airport" : opt.label,
                value: opt.radius,
                change: opt.change || { percent: 0, dollars: 0 },
                alternateInfo: {
                  departureOptions: opt.departureAlternates || [],
                  arrivalOptions: opt.arrivalAlternates || [],
                },
                flexibleEnd: aircraft.toggleOptions?.alternateAirports?.flexibleAirportEnd,
                isExact: opt.radius === 0,
              }))}
              selectedValue={selectedOptions.airportRadius}
              onSelect={(option) => handleOptionSelect("airportRadius", option)}
              departureAirport={departureAirportCode}
              arrivalAirport={arrivalAirportCode}
            />

            {/* YOM (Year of Manufacture) */}
            {aircraft.toggleOptions?.yom?.options?.length > 0 && (
              <DiscountToggle
                category="Aircraft Year"
                options={aircraft.toggleOptions.yom.options.map((opt) => ({
                  label: `${opt.year}${opt.isDefault ? " (Default)" : ""}`,
                  value: opt.year,
                  year: opt.year,
                  change: opt.change || { percent: 0, dollars: 0 },
                  isIncrease: opt.isIncrease,
                  isDefault: opt.isDefault,
                  isDiscount: opt.isDiscount,
                  wifi: opt.wifi,
                }))}
                selectedValue={selectedOptions.yom}
                onSelect={(option) => {
                  handleOptionSelect("yom", option);
                  // Reset WiFi when YOM changes
                  setSelectedOptions((prev) => ({ ...prev, wifiEnabled: false }));
                }}
              />
            )}

            {/* WiFi Add-On Toggle (shown only if WiFi available for selected YOM) */}
            {selectedOptions.yom?.wifi?.available && (
              <div className="discount-toggle">
                <label className="toggle-label">WiFi Add-On</label>
                <div className="toggle-options">
                  <div
                    className={`option ${
                      !selectedOptions.wifiEnabled ? "selected" : ""
                    }`}
                    onClick={() =>
                      setSelectedOptions((prev) => ({
                        ...prev,
                        wifiEnabled: false,
                      }))
                    }
                  >
                    <div className="option-label">No WiFi</div>
                    <div className="option-savings">
                      <span className="neutral">$0</span>
                    </div>
                  </div>
                  <div
                    className={`option increase-option ${
                      selectedOptions.wifiEnabled ? "selected" : ""
                    }`}
                    onClick={() =>
                      setSelectedOptions((prev) => ({
                        ...prev,
                        wifiEnabled: true,
                      }))
                    }
                  >
                    <div className="option-label">Add WiFi</div>
                    <div className="option-savings">
                      <span className="increase">
                        +{formatCurrency(selectedOptions.yom.wifi.addOnDollars)} (
                        {formatPercent(selectedOptions.yom.wifi.addOnPercent)})
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Departure Time - Per-leg for round trips */}
            {tripType === 2 ? (
              <>
                {/* Leg 1 - Departure */}
                <DiscountToggle
                  category="Departure Time - Leg 1 (Outbound)"
                  options={(
                    aircraft.toggleOptions?.departureTime?.leg1Options || []
                  ).map((opt, idx) => ({
                    label: opt.label,
                    value: opt.type === "flexible" ? `${opt.type}-${idx}` : opt.type,
                    change: opt.change || { percent: 0, dollars: 0 },
                    timeWindow: opt.timeWindow,
                    type: opt.type,
                  }))}
                  selectedValue={selectedOptions.departureTimeLeg1}
                  onSelect={(option) => handleOptionSelect("departureTimeLeg1", option)}
                  showTimePicker={true}
                />
                {/* Leg 2 - Return */}
                <DiscountToggle
                  category="Departure Time - Leg 2 (Return)"
                  options={(
                    aircraft.toggleOptions?.departureTime?.leg2Options || []
                  ).map((opt, idx) => ({
                    label: opt.label,
                    value: opt.type === "flexible" ? `${opt.type}-${idx}` : opt.type,
                    change: opt.change || { percent: 0, dollars: 0 },
                    timeWindow: opt.timeWindow,
                    type: opt.type,
                  }))}
                  selectedValue={selectedOptions.departureTimeLeg2}
                  onSelect={(option) => handleOptionSelect("departureTimeLeg2", option)}
                  showTimePicker={true}
                />
              </>
            ) : (
              /* One-way - Single departure time toggle */
              <DiscountToggle
                category="Departure Time"
                options={(
                  aircraft.toggleOptions?.departureTime?.options || []
                ).map((opt, idx) => ({
                  label: opt.label,
                  value: opt.type === "flexible" ? `${opt.type}-${idx}` : opt.type,
                  change: opt.change || { percent: 0, dollars: 0 },
                  timeWindow: opt.timeWindow,
                  type: opt.type,
                }))}
                selectedValue={selectedOptions.departureTimeLeg1}
                onSelect={(option) => handleOptionSelect("departureTimeLeg1", option)}
                showTimePicker={true}
              />
            )}
          </div>

          <div className="selected-price-summary">
            <h4>Your Selected Price:</h4>
            <div className="final-price-display">
              <span className="price-label">Final Price:</span>
              <span className="price-value">
                {formatCurrency(calculatedPrice)}
              </span>
            </div>
            {calculatedPrice < aircraft.defaultPrice && (
              <div className="total-savings">
                Total Savings:{" "}
                {formatCurrency(aircraft.defaultPrice - calculatedPrice)}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AircraftPricingCard;
